<template>
  <div v-if="userHasAccess" class="p-2 sm:p-4">
    <template v-if="isUnLocked">
      <div class='flex justify-between items-center'>
        <div>
          <h1>Financial</h1>
          <button v-if="oweApplicationFee" class="btn btn-orange" @click="$store.dispatch('createStripeSession_redirect',stripeSessionParams)">Pay Application Fee: {{currencySymbol}}{{Math.abs(totalApplicationFee - totalEventFeePaid).toFixed(2) }}</button>
          <button v-else-if="netOnline < 0" class="btn btn-orange" @click="$store.dispatch('createStripeSession_redirect',stripeSessionParams)">Pay Balance Due: {{currencySymbol}}{{Math.abs(netOnline).toFixed(2)}}</button>

        </div>
        <div class="flex flex-col text-right justify-end">
          <a class="link" @click="downloadFinancialData">Download Raw Payment Data</a>
          <a @click="downloadDetailedFinancialData" class='link'>Download Invoice Report</a>
          <a class="ml-2 link" :href="`/invoice?event_id=${event.event_id}&show_name=${encodeURIComponent(event.name)}&bulk=true`" target="_blank">Print All Invoices</a>
          <a :href="allInvoiceLink" target="_blank" class='link'>View Non Zero Balances</a>
          <!--<button @click="deleteExpressAccount">Delete Express</button>-->
        </div>
      </div>
      <div class="hr"></div>
      <div class="bg-gray-300 p-2">
        <p class="text-2xl font-light pl-2">Net Payments</p>
        <div class="flex flex-col md:flex-row justify-center" :class="{'hide-element': hideFinacials}">
          <div class="card m-2 text-left">
            <div class="flex items-center justify-center">
              <div class="mr-2">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="w-12 icon-currency-dollar"><circle cx="12" cy="12" r="11" class="primary"/><path class="secondary" d="M13 7.12v3.96c2.25.36 4 1.93 4 3.92 0 2-1.75 3.56-4 3.92V19a1 1 0 0 1-2 0v-.08a4.82 4.82 0 0 1-3.67-2.49 1 1 0 0 1 1.8-.85c.29.6.98 1.09 1.87 1.3v-3.96C8.75 12.56 7 11 7 9c0-2 1.75-3.56 4-3.92V5a1 1 0 0 1 2 0v.08a4.81 4.81 0 0 1 3.68 2.5 1 1 0 0 1-1.81.85c-.28-.6-.98-1.1-1.87-1.31zm-2 3.76V7.12C9.81 7.4 9 8.18 9 9c0 .82.81 1.6 2 1.88zm2 2.24v3.76c1.19-.28 2-1.06 2-1.88 0-.82-.81-1.6-2-1.88z"/></svg>
              </div>
              <div>
                <p class="font-light text-gray-600 text-sm">Net Total</p>
                <p class="text-2xl font-bold">{{currencySymbol}}{{netTotal}}</p>
              </div>
            </div>
          </div>
          <div class="card m-2 text-left">
            <div class="flex items-center justify-center">
              <div class="mr-2">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="w-12 icon-credit-card"><path class="primary" d="M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6c0-1.1.9-2 2-2zm13 12a1 1 0 0 0 0 2h2a1 1 0 0 0 0-2h-2zm-6 0a1 1 0 0 0 0 2h2a1 1 0 0 0 0-2h-2z"/><rect width="20" height="4" x="2" y="7" class="secondary"/></svg>
              </div>
              <div>
                <p class="font-light text-gray-600 text-sm">Net Online</p>
                <p class="text-2xl font-bold">{{currencySymbol}}{{netOnline.toFixed(2)}}</p>
              </div>
            </div>
          </div>
          <div class="card m-2 text-left">
            <div class="flex items-center justify-center">
              <div class="mr-2">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="w-12 icon-money"><path class="secondary" d="M18 14.74a4 4 0 0 0-8 .26H3a2 2 0 0 1-2-2V5c0-1.1.9-2 2-2h14a2 2 0 0 1 2 2v8a2 2 0 0 1-1 1.74zM10 12a3 3 0 1 0 0-6 3 3 0 0 0 0 6z"/><path class="primary" d="M7 9h14a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-8c0-1.1.9-2 2-2zm7 9a3 3 0 1 0 0-6 3 3 0 0 0 0 6z"/></svg>
              </div>
              <div>
                <p class="font-light text-gray-600 text-sm">Net Offline</p>
                <p class="text-2xl font-bold">{{currencySymbol}}{{offlineTotals.net}}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="hr"></div>
      <tabs>
        <tab name="Online Payments" selected="true">
          <div class="flex flex-wrap justify-between p-3">
            <div id='cc-gross' class="m-2" :class="{'hide-element': hideFinacials}">
              <p class="font-light text-gray-700 text-sm">Gross</p>
              <p class="text-2xl font-bold">{{currencySymbol}}{{creditCardTotals.gross.toFixed(2)}}</p>
            </div>
            <div id='cc-fee' class="m-2">
              <p class="font-light text-gray-700 text-sm">Fees Withheld</p>
              <p class="text-2xl font-bold">
                {{currencySymbol}}{{feeData.totalFeeOwed.toFixed(2)}}
              </p>
              <p class="text-sm text-gray-700 font-medium" v-if="feeData.showPaymentTotal">Paid - ({{currencySymbol}}{{feeData.showPaymentTotal.toFixed(2)}})</p>
              <button class="text-sm link" @click="$refs.viewFees.show()">View Details</button>
            </div>
            <div id='cc-net' class='m-2' :class="{'hide-element': hideFinacials}">
              <p class="font-light text-gray-700 text-sm">Net</p>
              <p class="text-2xl font-bold">{{currencySymbol}}{{netOnline.toFixed(2)}}</p>
            </div>
            <div id='cc-payout' class="m-2" :class="{'hide-element': hideFinacials}">
              <p class="font-light text-gray-700 text-sm">Payouts</p>
              <p class="text-2xl font-bold">{{currencySymbol}}{{payoutTotal}}</p>
              <button class="text-sm link" @click="$refs.viewPayouts.show()">View Details</button>
              <RecordManualShowPayout v-if="currentUser.user_id === 'Clo1Va5v4pNuvE1TqHPNeKWFGay2'" class="mt-1" :event_id="event.event_id" @checkRecorded="addCheckToPayouts"/>
            </div>
          </div>
          <div class="hr"></div>
          <div class="flex justify-center text-center mb-2">
            <ConnectAccount type='events' :connectAccount="adminData.connect_account" :return_url="`/shows#/${this.event.event_identifier}/admin`" :uuid="this.event.event_id" :country="this.event.location_details.country" @resetConnectAccount="adminData.connect_account = null"/>
          </div>
          <showman-table :condensed="true" v-if="onlinePayments.length" :tableData="onlinePayments" :hover="true" :tableFields="[{key:'Payment'},{key:'Net Amount'}]">
            <div slot="Payment" slot-scope="data">
              <template v-if="data.item.sponsor">
                <p class="font-light text-xl">{{`${data.item.sponsor.sponsor_details.name}`}}</p>
                <p class="text-xs text-gray-700">
                  <span v-if="data.item.sponsor.sponsor_details.contact_name" class="text-base font-light">{{data.item.sponsor.sponsor_details.contact_name}}</span>
                  <span class="font-medium"> ~ {{formatDateTime(data.item.date_paid)}} ~ </span>
                  <span class="font-bold text-orange-500">Sponsorship</span></p>
              </template>
              <template v-else-if="data.item.event_sponsor">
                <p class="font-light text-xl">{{`${data.item.event_sponsor.sponsor_name}`}}</p>
                <p class="text-xs text-gray-700">
                  <span v-if="data.item.event_sponsor.contact_name" class="text-base font-light">{{data.item.event_sponsor.contact_name}}</span>
                  <span class="font-medium"> ~ {{formatDateTime(data.item.date_paid)}} ~ </span>
                  <span class="font-bold text-orange-500">Sponsorship</span></p>
              </template>
              <template v-else-if="data.item.user">
                <p class="font-light text-xl">{{`${data.item.user.first_name} ${data.item.user.last_name}`}}</p>
                <p class="text-xs text-gray-700">
                  <span class="text-base font-light" v-if="data.item.user.farm_name">{{data.item.user.farm_name}}</span>
                  <span class="font-medium"> ~ {{formatDateTime(data.item.date_paid)}}</span>
                </p>
              </template>
            </div>
            <div class="justify-end flex flex-col md:flex-row items-center" slot="Net Amount" slot-scope="data">
              <div class="text-right mr-0 md:mr-2">
                <template v-if="data.item.show_credit_card_fee || data.item.platform_fee">
                  <p class="font-medium text-sm sm:text-base">Net: {{currencySymbol}}{{(data.item.show_amount - data.item.show_credit_card_fee - data.item.platform_fee).toFixed(2)}}</p>
                  <p class="font-regular text-gray-700 text-xs sm:text-sm" >Gross: {{currencySymbol}}{{(data.item.show_amount).toFixed(2)}}</p>
                </template>
                <template v-else>
                  <p class="font-medium sm:text-lg" >{{currencySymbol}}{{(data.item.show_amount).toFixed(2)}}</p>
                </template>
              </div>
              <button v-if="currentUser.user_id === 'Clo1Va5v4pNuvE1TqHPNeKWFGay2'" class='btn-xs btn-orange' @click="openRefundPayment(data.item)">Refund</button>
            </div>
        </showman-table>
        </tab>
        <tab name="Offline Payments">
          <div class="flex flex-wrap justify-between p-3" :class="{'hide-element': hideFinacials}">
            <div id='offline-gross' class="m-2">
              <p class="font-light text-gray-700 text-sm">Total Collected</p>
              <p class="text-2xl font-bold">{{currencySymbol}}{{offlineTotals.net}}</p>
            </div>
            <div id='offline-cash' class="m-2">
              <p class="font-light text-gray-700 text-sm">Cash</p>
              <p class="text-2xl font-bold">{{currencySymbol}}{{offlineTotals.cash}}</p>
            </div>
            <div id='offline-check' class='m-2'>
              <p class="font-light text-gray-700 text-sm">Check</p>
              <p class="text-2xl font-bold">{{currencySymbol}}{{offlineTotals.check}}</p>
            </div>
            <div id="offline-cc" class="m-2">
              <p class="font-light text-gray-700 text-sm">CC Other</p>
              <p class="text-2xl font-bold">{{currencySymbol}}{{offlineTotals.cc_other}}</p>
            </div>
            <div id="offline-cc" class="m-2">
              <p class="font-light text-gray-700 text-sm">Show Credit Given</p>
              <p class="text-2xl font-bold">{{currencySymbol}}{{offlineTotals.show_credit}}</p>
            </div>
          </div>
          <div class="hr"></div>
          <entry-credits />
        </tab>
      </tabs>

      <b-modal id="viewPayouts" ref="viewPayouts" size="lg" :title="`Total Payouts: ${currencySymbol}${payoutTotal}`" >
        <showman-table :tableData="adminData.payouts.filter(p => p.type !== 'APPLICATION_FEE')" :tableFields="[{key:' '}]" :showHeaders="false" :hover="true">
          <div slot=" " slot-scope="data" class="flex justify-between items center">
            <div>
              <div class="font-thin text-xl">{{formatDateTime(data.item.date)}}</div>
              <div class="font-light text-gray-700">{{data.item.type}}</div>
            </div>
            <div class="text-right text-xl font-heavy">
              <p>{{currencySymbol}}{{data.item.amount.toFixed(2)}}</p>
              <p>{{data.item.status}}</p>
            </div>
          </div>
        </showman-table>
        <div slot="modal-footer" class="w-100">
          <button class="btn-gray-outline btn-sm" variant="primary" @click="$refs.viewPayouts.hide()">
            close
          </button>
        </div>
      </b-modal>

      <b-modal ref="viewFees" size="lg" title="Invoice Details" v-if="adminData">
        <div class="flex justify-between mb-4">
          <div>
            <p class="text-sm">Bill To:</p>
            {{ event.name }}
          </div>
          <div class="flex justify-start items-start space-x-3">
            <img src="https://res.cloudinary.com/showman/image/upload/h_55/v1671228923/Showman-Banner-Orange.png">
            <div class="text-sm">
              <p>Maynetainable, LLC</p>
              <p>8602 Green Valley Rd.</p>
              <p>Union Bridge, MD 21791</p>
            </div>
          </div>
        </div>
        <dl class="space-y-4">
          <div class="p-4 bg-gray-100 rounded-md border">
            <p class="font-bold">SHOWMAN FEES</p>
            <div class="py-2 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt class="text-sm font-medium text-gray-700">
                Base Application Fee
              </dt>
              <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {{currencySymbol}}{{feeData.base_price.toFixed(2)}}
              </dd>
              <template v-if="feeData.extraEntryFee">
                <dt class="text-sm font-medium text-gray-700">
                  <p>Additional Entries Fee</p>
                  <span class="text-xs">Over {{ adminData.base_entries }} entries</span>
                </dt>
                <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  {{currencySymbol}}{{(feeData.extraEntryFee).toFixed(2)}}
                </dd>
              </template>
              <template v-if="feeData.add_on_fees">
                <dt class="text-sm font-medium text-gray-700">
                  <p>Add-On's</p>
                  <span class="text-xs">{{ feeData.add_on_notes }}</span>
                </dt>
                <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  {{currencySymbol}}{{(feeData.add_on_fees).toFixed(2)}}
                </dd>
              </template>
              <template v-if="feeData.platformFeeTotal">
                <dt class="text-sm font-medium text-gray-700">
                  Platform Fee
                </dt>
                <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  {{currencySymbol}}{{feeData.platformFeeTotal.toFixed(2)}}
                </dd>
              </template>
            </div>
            <div class="py-2 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 border-t border-gray-300">
              <dt class="text-base font-medium text-gray-800">
                Total
              </dt>
              <dd class="mt-1 text-base text-gray-900 sm:mt-0 sm:col-span-2">
                {{currencySymbol}}{{feeData.totalApplicationFee.toFixed(2)}}
              </dd>
            </div>
          </div>
          <div class="p-4 bg-gray-100 rounded-md border" v-if="adminData.show_payments.length">
            <p class="font-bold">PAYMENTS MADE</p>
            <div class="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6" v-for="(p,i) in adminData.show_payments" :key="i" >
              <dt class="text-sm font-medium text-gray-600">
                {{Localized.formatDate(p.date_paid)}}
              </dt>
              <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                <!-- Heroicon name: solid/paper-clip -->
                <span class="flex-1 w-0 truncate">
                  ({{currencySymbol}}{{p.total_amount}})
                </span>
              </dd>
            </div>
          </div>
          <div class="p-4 bg-gray-100 rounded-md border" v-if="feeData.ccFeeTotal">
            <p class="font-bold">OTHER FEES</p>
            <div class="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt class="text-base font-medium text-gray-800">
                <p>Credit Card Fees</p>
                <span class="text-xs">Stripe processing fees</span>
              </dt>
              <dd class="mt-1 text-base text-gray-900 sm:mt-0 sm:col-span-2">
                {{currencySymbol}}{{feeData.ccFeeTotal.toFixed(2)}}
              </dd>
            </div>
          </div>
          <div class="bg-gray-100 rounded-md border">
            <div class="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt class="text-lg font-medium text-gray-900">
                Total amount:
              </dt>
              <dd class="mt-1 text-lg text-gray-900 sm:mt-0 sm:col-span-2 font-bold">
                {{currencySymbol}}{{feeData.totalFeeOwed.toFixed(2)}}
              </dd>
              <p class="text-xs text-gray-900 font-medium">If paying via check Please make payable to <i>Maynetainable, LLC</i> and send to the address at the top.</p>
            </div>
          </div>
        </dl>
        <div class="md:px-8 my-6">
          <p class="text-center font-bold text-gray-800">
            A total of {{currencySymbol}}{{feeData.totalFeeOwed.toFixed(2)}} of the remaining fees owed will be taken out of your Gross online processing before being sent to your bank account, see details above.
          </p>
        </div>
        <div slot="modal-footer" class="w-100">
          <button class="btn-gray-outline btn-sm" variant="primary" @click="$refs.viewFees.hide()">
            close
          </button>
        </div>
      </b-modal>

      <b-modal ref="issueRefund" size="lg" :title="`Issue Refund`" no-close-on-backdrop>
        <template v-if="refundPayment">
          <template v-if="refundPayment.user">
            <div>{{refundPayment.user.first_name}} {{refundPayment.user.last_name}}</div>
            <div>{{refundPayment.user.email}}</div>
          </template>
          <div>Total Amount: {{refundPayment.total_amount}}</div>
          <div>Show Amount: {{refundPayment.show_amount}}</div>
          <div>Show Fees: {{refundPayment.show_credit_card_fee}}</div>
          <div class="mt-3">
            Refund Amount
            <input class="text-input" v-model.number="refundAmount" type="number">
            **The credit card fees is non refundable. Any difference between the amount refunded and the "Show Amount" will be converted into "Show Fees"
            <!--Take Credit Card Fees from
            <select v-model="takeFeesFrom" class="block appearance-none w-full bg-gray-300 border border-gray-300 text-gray-700 py-3 px-4 pr-8 rounded leading-tight focus:outline-none focus:bg-white focus:border-gray-500" id="grid-state">
              <option :value="null" selected disabled>
                Please select a value
              </option>
              <option value="SHOW">
                Show ()
              </option>
              <option value="EXHIBITOR">
                Exhibitor
              </option>
            </select>-->
          </div>
          <button class="btn btn-blue" @click="issueRefund()">Issue Refund</button>
        </template>
        <div slot="modal-footer" class="w-100">
          <button class="btn-red btn-xs" variant="primary" @click="$refs.issueRefund.hide()">
            Cancel
          </button>
        </div>
      </b-modal>
    </template>
    <template v-else>
        <h1>Financial</h1>
        <div class="mt-2 w-full md:w-2/3">
          <input v-model="enteredPassword" class="border rounded w-full p-2" placeholder="Enter Password">
          <p class="text-lg mt-2">Please enter the secret password to view the Financial Data.</p>
        </div>
    </template>
  </div>
  <div v-else>
    <NoAccess :section="'Financial'" :admin_email="adminData.show_admin.email"/>
  </div>
</template>

<script>
import ConnectAccount from '@/components/ConnectAccount_new'
import EntryCredits from '@/components/admin/EntryCredits'
import ExportData from '@/assets/exportData.js'
import RecordManualShowPayout from '@/components/superAdmin/RecordManualShowPayout'
import NoAccess from '@/views/admin/NoAccess.vue'

export default {
  components: {
    ConnectAccount,
    EntryCredits,
    RecordManualShowPayout,
    NoAccess
  },
  data () {
    return {
      refundPayment: null,
      refundAmount: 0,
      takeFeesFrom: '',
      enteredPassword: ''
    }
  },
  methods: {
    formatDateTime (date) {
      return this.Localized.formatDateTime(date, this.event.location_details.country)
    },
    downloadFinancialData () {
      ExportData.exportCSVFile(`${this.adminData.event.name}_financial_data`, this.downloadData, this.financialHeaders)
    },
    openRefundPayment (payment) {
      this.refundPayment = payment
      this.$refs.issueRefund.show()
    },
    issueRefund () {
      this.$store.dispatch('refundCharge', {
        payment_intent: this.refundPayment.payment_intent,
        refund_amount: this.refundAmount,
        payment_id: this.refundPayment.payment_id,
        show_amount: this.refundPayment.show_amount,
        total_amount: this.refundPayment.total_amount,
        show_credit_card_fee: this.refundPayment.show_credit_card_fee
      })
        .then(response => {
          this.refundPayment.show_amount = this.refundPayment.show_amount - this.refundAmount
          this.refundPayment.total_amount = this.refundPayment.total_amount - this.refundAmount

          this.refundPayment = null
          this.refundAmount = 0
          alert('Refund Issued')
          this.$refs.issueRefund.hide()
        })
        .catch(error => {
          alert(error)
          // console.log(error)
        })
    },
    downloadDetailedFinancialData () {
      this.axios.get(process.env.VUE_APP_ROOT_API + `/invoice/detailed-financial-report?event_id=${this.adminData.event.event_id}`)
        .then(results => {
          ExportData.exportCSVFile(`${this.adminData.event.name}_detailed_financial_data`, results.data.data, results.data.fields)
        })
    },
    addCheckToPayouts (payout) {
      this.adminData.payouts.push(payout)
    }
    /* deleteExpressAccount () {
      this.$store.state.apiInstance.post('/stripe/express/delete', {})
        .then(response => {
          // console.log(response)
        })
        .catch(e => {
          // console.log(e)
        })
    } */
  },
  computed: {
    currentUser () {
      return this.$store.state.user
    },
    adminData () {
      return this.$store.state.admin.adminData
    },
    userHasAccess () {
      let access = false
      if (this.adminData.admin_id === this.currentUser.user_id || this.currentUser.user_id === 'Clo1Va5v4pNuvE1TqHPNeKWFGay2') {
        access = true
      } else {
        this.adminData.event_user_access.forEach(ur => {
          if (ur.user_id === this.currentUser.user_id) {
            access = ur.financial_access
          }
        })
      }
      if (this.currentUser.role === 'ADMIN') {
        access = true
      }
      // For financials hide accees if not explicitly granted
      /* if (!foundUser) {
        if (this.adminData.admin_ids.includes(this.currentUser.user_id)) {
          access = true
        }
      } */
      return access
    },
    event () {
      return this.adminData.event
    },
    totalApplicationFee () {
      let fee = this.adminData.event.event_settings.add_on_fees ? (this.adminData.event_fee + this.adminData.event.event_settings.add_on_fees) : this.adminData.event_fee
      return fee * this.Localized.getCurrencyInfo(this.event.location_details.country).exchangeRate
    },
    extraEntryFee () {
      return this.adminData.event_fee - this.adminData.base_price
    },
    totalEventFeePaid () {
      return this.adminData.show_payments.reduce((total, payment) => {
        return total + payment.total_amount
      }, 0)
      // return this.adminData.event_fee_paid ? this.adminData.event_fee_paid : 0
    },
    oweApplicationFee () {
      // if (this.adminData.event.event_settings.payment_type === 'CC' && this.adminData.show_payments.length === 0) {
      if (this.adminData.event.event_settings.payment_type === 'CC' && this.feeData.totalFeeOwed) {
        return true
      } else {
        return false
      }
    },
    allInvoiceLink () {
      return `${process.env.VUE_APP_ROOT_API}/invoice/all-invoice?event_id=${this.adminData.event.event_id}`
    },
    allPayments () {
      return this.adminData.payments.filter(p => !(p.type === 'CREDIT' && p.payment_type === 'SHOW_CREDIT'))
    },
    onlinePayments () {
      return this.adminData.payments.filter(p => p.type === 'STRIPE')
    },
    offlinePayments () {
      return this.adminData.payments.filter(p => (p.type === 'CREDIT' && p.payment_type !== 'SHOW_CREDIT'))
    },
    creditCardTotals () {
      return {
        gross: this.onlinePayments.reduce((total, currentValue, currentIndex, array) => {
          total += Number(currentValue.show_amount)
          return total
        }, 0) // .toFixed(2)
      }
    },
    feeData () {
      let feeData = {
        ccFeeTotal: 0,
        platformFeeTotal: 0,
        totalApplicationFee: this.totalApplicationFee,
        extraEntryFee: this.extraEntryFee,
        base_price: this.adminData.base_price,
        showPaymentTotal: 0,
        add_on_fees: this.adminData.event.event_settings.add_on_fees ? this.adminData.event.event_settings.add_on_fees : 0,
        add_on_notes: this.adminData.event.event_settings.add_on_notes
      }
      feeData.items = this.onlinePayments.forEach(p => {
        if (p.show_credit_card_fee) {
          feeData.ccFeeTotal += Number(p.show_credit_card_fee)
        }
        if (p.platform_fee) {
          feeData.platformFeeTotal += Number(p.platform_fee)
          feeData.totalApplicationFee += Number(p.platform_fee)
        }
      })
      feeData.total = (feeData.ccFeeTotal + feeData.platformFeeTotal + this.totalApplicationFee) // .toFixed(2)

      // Find Application Fee Payments
      this.adminData.show_payments.forEach(p => {
        feeData.showPaymentTotal += p.total_amount
      })
      // feeData.showPaymentTotal = feeData.showPaymentTotal.toFixed(2)
      feeData.totalFeeOwed = (feeData.total - feeData.showPaymentTotal) // .toFixed(2)

      return feeData
    },
    netOnline () {
      return (this.creditCardTotals.gross - this.feeData.totalFeeOwed) // .toFixed(2)
    },
    netTotal () {
      let total = this.netOnline + Number(this.offlineTotals.net)
      return total.toFixed(2)
    },
    offlineTotals () {
      return {
        gross: this.offlinePayments.reduce((total, current) => {
          if (current.details.payment_type !== 'SHOW_CREDIT') {
            total += Number(current.show_amount)
          }
          return total
        }, 0).toFixed(2),
        net: this.offlinePayments.reduce((total, current) => {
          if (current.details.payment_type !== 'SHOW_CREDIT') {
            total += (Number(current.show_amount))
          }
          return total
        }, 0).toFixed(2),
        cash: this.offlinePayments.reduce((total, current) => {
          if (current.details.payment_type === 'CASH') {
            total += Number(current.show_amount)
          }
          return total
        }, 0).toFixed(2),
        check: this.offlinePayments.reduce((total, current) => {
          if (current.details.payment_type === 'CHECK') {
            total += Number(current.show_amount)
          }
          return total
        }, 0).toFixed(2),
        cc_other: this.offlinePayments.reduce((total, current) => {
          if (current.details.payment_type === 'CC_OTHER') {
            total += Number(current.show_amount)
          }
          return total
        }, 0).toFixed(2),
        show_credit: this.offlinePayments.reduce((total, current) => {
          if (current.details.payment_type === 'SHOW_CREDIT') {
            total += Number(current.show_amount)
          }
          return total
        }, 0).toFixed(2)
      }
    },
    financialTotals () {
      return {
        gross: this.allPayments.reduce((total, currentValue, currentIndex, array) => {
          total += Number(currentValue.show_amount)
          return total
        }, 0).toFixed(2),
        net: this.allPayments.reduce((total, current) => {
          total += (Number(current.show_amount) - Number(current.show_credit_card_fee ? current.show_credit_card_fee : 0) - Number(current.platform_fee ? current.platform_fee : 0))
          return total
        }, 0).toFixed(2)
      }
    },
    payoutTotal () {
      return this.adminData.payouts.reduce((total, current) => {
        if (current.type !== 'APPLICATION_FEE' && current.status === 'PAID') {
          total += Number(current.amount)
        }
        return total
      }, 0).toFixed(2)
    },
    downloadData () {
      return this.allPayments.map(payment => {
        let p = {...payment}

        if (p.user) {
          p.last_name = p.user.last_name
          p.first_name = p.user.first_name
          p.email = p.user.email
        }
        p.net_amount = p.show_amount - p.show_credit_card_fee
        if (p.type === 'STRIPE') {
          p.payment_type = 'CREDIT CARD'
        } else if (p.type === 'CREDIT') {
          p.payment_type = p.details.payment_type
        }
        p.description = p.details.description
        p.date_paid = this.Localized.formatDate(p.date_paid, this.event.location_details.country)
        return p
      })
    },
    financialHeaders () {
      let headers = [
        {
          field: 'email',
          header: 'Email'
        },
        {
          field: 'last_name',
          header: 'Last Name'
        },
        {
          field: 'first_name',
          header: 'First Name'
        },
        {
          field: 'show_amount',
          header: 'Amount Paid'
        },
        {
          field: 'show_credit_card_fee',
          header: 'Fees'
        },
        {
          field: 'net_amount',
          header: 'Net'
        },
        {
          field: 'payment_type',
          header: 'Payment Type'
        },
        {
          field: 'description',
          header: 'Description'
        },
        {
          field: 'date_paid',
          header: 'Date Paid'
        }
      ]
      return headers
    },
    isUnLocked () {
      if (!this.adminData.event.event_settings.financial_tab_password) {
        return true
      } else {
        return this.adminData.event.event_settings.financial_tab_password === this.enteredPassword
      }
    },
    stripeSessionParams () {
      return {
        type: 'NEW_EVENT',
        applicationFee: this.oweApplicationFee ? (this.totalApplicationFee - this.totalEventFeePaid).toFixed(2) : 0,
        netOnline: this.netOnline.toFixed(2),
        event_id: this.event.event_id,
        event_name: this.event.name,
        event_identifier: this.event.event_identifier,
        event_type: this.event.event_settings.event_type,
        email: this.currentUser.email,
        user_id: this.currentUser.user_id,
        stripe_customer_id: this.currentUser.stripe_customer_id,
        success_url: `${process.env.VUE_APP_ROOT_URL}/shows#/${this.event.event_identifier}/admin/financial`,
        cancel_url: `${process.env.VUE_APP_ROOT_URL}/shows#/${this.event.event_identifier}/admin/financial`
      }
    },
    hideFinacials () {
      if (this.currentUser.user_id === 'Clo1Va5v4pNuvE1TqHPNeKWFGay2') {
        return false
      } else if (this.currentUser.user_id === 'WK7NrDb812UPUK2x7VZynNArOIo2' || this.currentUser.user_id === 'AmtbH8WVMsOneNemxsGcShyc9IE2' || this.currentUser.user_id === '2S1n15f3I0OJjgpLx5pMlHdAfT13' || this.currentUser.user_id === '9tWzGNjoUZWFjonz2PJJpAnkGxg1' || this.currentUser.user_id === '5kpEsEowxGNSsdpPjQpS3PoL2g02' || this.currentUser.user_id === 'r1noJUdvSoZX6LIesj9eBNA5yEx1') {
        return true
      } else {
        return false
      }
    },
    currencySymbol () {
      return this.Localized.getCurrencyInfo(this.event.location_details.country).symbol
    }
  }
}
</script>

<style>
  .hide-element {
    filter: blur(4.5px);
  }
</style>
